import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '@/locales/translations';
import api from '@/services/api';
const STATIC_LANGUAGES = ['pt', 'en', 'es', 'fr'];

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const useTranslation = () => {
  const { translationDict } = useLanguage();
  const t = useCallback((key) => {
    return translationDict[key] || translations['pt'][key] || key;
  }, [translationDict]);
  return { t };
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'pt';
  });
  const [translationDict, setTranslationDict] = useState(() => {
    const saved = localStorage.getItem('language') || 'pt';
    if (STATIC_LANGUAGES.includes(saved)) {
      return translations[saved] || translations['pt'];
    }
    const cached = localStorage.getItem(`translations_${saved}`);
    if (cached) {
      try { return JSON.parse(cached); } catch (e) { return translations['pt']; }
    }
    return translations['pt'];
  });
  const [isTranslating, setIsTranslating] = useState(false);

  const fetchDynamicTranslations = useCallback(async (lang) => {
    if (STATIC_LANGUAGES.includes(lang)) {
      setTranslationDict(translations[lang] || translations['pt']);
      return;
    }

    const cached = localStorage.getItem(`translations_${lang}`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Object.keys(parsed).length > 10) {
          setTranslationDict(parsed);
          return;
        }
      } catch (e) { }
    }

    setIsTranslating(true);
    try {
      const ptDict = translations['pt'];
      const response = await api.post('/translate/ui-dictionary', {
        dictionary: ptDict,
        target_language: lang
      }, { timeout: 60000 });

      const translated = response.data?.translations || ptDict;
      setTranslationDict(translated);
      localStorage.setItem(`translations_${lang}`, JSON.stringify(translated));
    } catch (err) {
      setTranslationDict(translations['pt']);
    } finally {
      setIsTranslating(false);
    }
  }, []);

  useEffect(() => {
    fetchDynamicTranslations(language);
  }, [language, fetchDynamicTranslations]);

  const changeLanguage = useCallback((newLanguage) => {
    if (newLanguage && newLanguage !== language) {
      setLanguage(newLanguage);
      localStorage.setItem('language', newLanguage);
    }
  }, [language]);

  const value = {
    language,
    changeLanguage,
    isTranslating,
    translationDict,
    t: (key) => translationDict[key] || translations['pt'][key] || key
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
